import { FC, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { Outlet, useNavigate } from 'react-router-dom';

import { Box, Grid, rem, useBreakpointValue } from '@bonusly/design-system';
import { useFeatureFlag, Variant } from '@bonusly/who-am-i';

import { App as AdminNav } from '../../modules/admin-navbar/components/app/app';
import { useCelebrationEventTracking } from '../../modules/celebrations/hooks';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { AdminRoute } from '../routes/AdminRoute';
import { useSpoofing } from './hooks/useSpoofing';

const { GlobalModals } = lazily(() => import('../../app/global-modals'));
const { BizyWidget } = lazily(() => import('../bizy-widget'));

export const AdminAppLayout: FC = () => {
	// Enable celebration event tracking for Intercom integration
	useCelebrationEventTracking();

	const { data } = useSpoofing();
	const navigate = useNavigate();
	const isMobile = useBreakpointValue({ base: true, xs: false });
	const hasBizyForParticipation = useFeatureFlag(Variant.BizyForParticipation);

	if (isMobile) {
		navigate('/mobile-unavailable');
		return null;
	}

	return (
		<>
			<Suspense>
				<GlobalModals />
			</Suspense>

			<Box pos="relative">
				{data?.is_spoofing && !data?.is_demoing && <SpoofingWarning />}
				<Grid
					bg="bg.subtle"
					h="100vh"
					minW={rem(370)}
					gridTemplateColumns={{
						base: '1fr',
						xs: `${rem(200)} 1fr`,
					}}
				>
					<AdminNav h="100vh" />
					<Box as="main" overflowY="auto" h="100vh">
						<AdminRoute>
							<Suspense>
								<Outlet />
							</Suspense>
						</AdminRoute>
					</Box>
				</Grid>

				{/* Bizy widget - floating chat for admin pages */}
				{hasBizyForParticipation && (
					<Suspense fallback={null}>
						<BizyWidget />
					</Suspense>
				)}
			</Box>
		</>
	);
};
